<script setup lang="ts">
import type { Subscription } from '@entities/subscription'

import {
  faArrowUpArrowDown,
  faArrowUpRight,
  faArrowsRotate,
  faBadgePercent,
  faBan,
  faCalendarPen,
  faCirclePause,
  faCreditCard,
  faCrown,
  faEye,
  faLinkSimple,
  faListCheck,
  faPowerOff,
  faRotate,
  faSplit,
} from '@fortawesome/pro-regular-svg-icons'

import { SubscriptionModal } from '@entities/subscription'
import { AppDropdown, AppDropdownItem } from '@shared/ui/dropdowns'
import { BtnIcon } from '@shared/ui/buttons'

import { useSubscriptionActions } from '../model/subscription-actions.hook'

defineOptions({
  name: 'SubscriptionRowActions',
})

const props = withDefaults(defineProps<{
  subscription: Subscription
  /** `manage-subscriptions`, resolved once by the table or modal rather than per row. */
  canManage: boolean
  /** Off inside the details modal, which is already what "View details" opens. */
  withDetails?: boolean
  /** "Create task" and "View related tasks", shown only beside the customer's own page as in legacy. */
  withTasks?: boolean
}>(), {
  withDetails: true,
  withTasks: false,
})

const {
  isOpen,
  containerRef,
  floatingRef,
  floatingStyles,
  triggerIcon,
  isProcessing,
  isChangeProductVisible,
  isChangeProductDisabled,
  isSplitVisible,
  isDiscountVisible,
  isChargeNowVisible,
  isChargeNowDisabled,
  chargeNowHint,
  isResumeVisible,
  isChangePauseVisible,
  isPauseVisible,
  isPremiumTemplateVisible,
  isCancelVisible,
  isTasksVisible,
  autoRenewText,
  autoRenewColor,
  toggle,
  openModal,
  openSplit,
  openChangelog,
  createTask,
  openRelatedTasks,
  syncSubscription,
} = useSubscriptionActions({
  subscription: computed(() => props.subscription),
  canManage: computed(() => props.canManage),
  withTasks: computed(() => props.withTasks),
})
</script>

<template>
  <div
    ref="containerRef"
    class="subscription-row-actions"
  >
    <BtnIcon
      :icon="triggerIcon"
      view="table"
      :title="`Actions for subscription #${subscription.id}`"
      @click="toggle"
    />
    <div
      v-if="isOpen"
      ref="floatingRef"
      class="subscription-row-actions__floating"
      :style="floatingStyles"
    >
      <AppDropdown>
        <AppDropdownItem
          v-if="withDetails"
          text="View details"
          color="blue"
          :prepend-icon="faEye"
          @click="openModal(SubscriptionModal.Details)"
        />
        <AppDropdownItem
          v-if="isChangeProductVisible"
          text="Change subscription product"
          color="blue"
          :prepend-icon="faArrowUpArrowDown"
          :disabled="isChangeProductDisabled"
          @click="openModal(SubscriptionModal.ChangeProduct)"
        />
        <AppDropdownItem
          v-if="isSplitVisible"
          text="Split subscription settings"
          color="blue"
          :prepend-icon="faSplit"
          @click="openSplit"
        />
        <AppDropdownItem
          v-if="isTasksVisible"
          text="Create task"
          color="blue"
          :prepend-icon="faListCheck"
          @click="createTask"
        />
        <AppDropdownItem
          v-if="isTasksVisible"
          text="View related tasks"
          color="blue"
          :prepend-icon="faArrowUpRight"
          @click="openRelatedTasks"
        />
        <AppDropdownItem
          v-if="canManage"
          text="Sync subscription"
          color="blue"
          :prepend-icon="faLinkSimple"
          :disabled="isProcessing"
          @click="syncSubscription"
        />
        <AppDropdownItem
          v-if="isDiscountVisible"
          text="Subscription discount"
          color="blue"
          :prepend-icon="faBadgePercent"
          @click="openModal(SubscriptionModal.Discount)"
        />
        <AppDropdownItem
          text="Subscription changelog"
          color="blue"
          :prepend-icon="faArrowsRotate"
          @click="openChangelog"
        />
        <AppDropdownItem
          v-if="isChargeNowVisible"
          text="Charge now"
          color="blue"
          :description="chargeNowHint"
          :prepend-icon="faCreditCard"
          :disabled="isChargeNowDisabled"
          @click="openModal(SubscriptionModal.ChargeNow)"
        />
        <AppDropdownItem
          v-if="isResumeVisible"
          text="Resume subscription"
          color="blue"
          :prepend-icon="faRotate"
          @click="openModal(SubscriptionModal.Resume)"
        />
        <AppDropdownItem
          v-if="isChangePauseVisible"
          text="Change pause"
          color="blue"
          :prepend-icon="faCalendarPen"
          @click="openModal(SubscriptionModal.ChangePause)"
        />
        <AppDropdownItem
          v-if="isPauseVisible"
          text="Pause subscription"
          color="blue"
          :prepend-icon="faCirclePause"
          @click="openModal(SubscriptionModal.Pause)"
        />
        <AppDropdownItem
          v-if="isPremiumTemplateVisible"
          text="Send Premium tutor template"
          color="blue"
          :prepend-icon="faCrown"
          @click="openModal(SubscriptionModal.PremiumTemplate)"
        />
        <AppDropdownItem
          v-if="canManage"
          :text="autoRenewText"
          :color="autoRenewColor"
          :prepend-icon="faPowerOff"
          @click="openModal(SubscriptionModal.AutoRenew)"
        />
        <AppDropdownItem
          v-if="isCancelVisible"
          text="Cancel subscription"
          color="red"
          :prepend-icon="faBan"
          @click="openModal(SubscriptionModal.Cancel)"
        />
      </AppDropdown>
    </div>
  </div>
</template>

<style scoped lang="scss" src="./SubscriptionRowActions.scss" />
